import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import axios from "axios";
import { Toaster } from "../ui/sonner";
import { toast } from "sonner";
import LoginPage from "./LoginPage";
import MainApp from "./MainApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context for Word Add-in
export const WordAddinAuthContext = createContext(null);

export const useWordAddinAuth = () => {
  const context = useContext(WordAddinAuthContext);
  if (!context) {
    throw new Error("useWordAddinAuth must be used within WordAddinAuthProvider");
  }
  return context;
};

const WordAddinAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("lexy_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Token verification failed:", error);
          localStorage.removeItem("lexy_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("lexy_token", newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      const lang = localStorage.getItem('lexy_plugin_lang') || 'nb';
      const message = error.response?.data?.detail || (lang === 'nb' ? "Innlogging feilet" : "Login failed");
      toast.error(lang === 'nb' ? "Feil" : "Error", { description: message });
      return { success: false, error: message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      // For Word add-in, we'll use a simpler approach:
      // 1. Open the main Lexy login page with Google auth
      // 2. User logs in via Google
      // 3. Token gets stored in localStorage
      // 4. Word add-in checks localStorage periodically
      
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      // Open the main Lexy login which handles Google OAuth properly
      const popup = window.open(
        `${process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}/lexy/login?provider=google&from=word-addin`,
        'Google Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Poll localStorage for token (set by main app after OAuth)
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const maxWaitTime = 5 * 60 * 1000; // 5 minutes max
        
        const checkToken = setInterval(() => {
          // Check if token appeared in localStorage
          const newToken = localStorage.getItem("lexy_token");
          if (newToken && newToken !== token) {
            clearInterval(checkToken);
            setToken(newToken);
            // Verify the token
            axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` }
            }).then(response => {
              setUser(response.data);
              toast.success("Logget inn med Google");
              resolve({ success: true });
            }).catch(() => {
              reject({ success: false });
            });
          }
          
          // Check if popup was closed
          if (popup?.closed) {
            clearInterval(checkToken);
            // One final check
            const finalToken = localStorage.getItem("lexy_token");
            if (finalToken && finalToken !== token) {
              setToken(finalToken);
              axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${finalToken}` }
              }).then(response => {
                setUser(response.data);
                toast.success("Logget inn med Google");
                resolve({ success: true });
              }).catch(() => {
                resolve({ success: false });
              });
            } else {
              resolve({ success: false });
            }
          }
          
          // Timeout after max wait time
          if (Date.now() - startTime > maxWaitTime) {
            clearInterval(checkToken);
            resolve({ success: false });
          }
        }, 1000);
      });
    } catch (error) {
      console.error("Google login error:", error);
      toast.error("Google innlogging feilet");
      return { success: false };
    }
  };

  const loginWithMicrosoft = async () => {
    try {
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      // Open the main Lexy login which handles Microsoft OAuth properly
      const popup = window.open(
        `${process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}/lexy/login?provider=microsoft&from=word-addin`,
        'Microsoft Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Poll localStorage for token
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const maxWaitTime = 5 * 60 * 1000;
        
        const checkToken = setInterval(() => {
          const newToken = localStorage.getItem("lexy_token");
          if (newToken && newToken !== token) {
            clearInterval(checkToken);
            setToken(newToken);
            axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` }
            }).then(response => {
              setUser(response.data);
              toast.success("Logget inn med Microsoft");
              resolve({ success: true });
            }).catch(() => {
              reject({ success: false });
            });
          }
          
          if (popup?.closed) {
            clearInterval(checkToken);
            const finalToken = localStorage.getItem("lexy_token");
            if (finalToken && finalToken !== token) {
              setToken(finalToken);
              axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${finalToken}` }
              }).then(response => {
                setUser(response.data);
                toast.success("Logget inn med Microsoft");
                resolve({ success: true });
              }).catch(() => {
                resolve({ success: false });
              });
            } else {
              resolve({ success: false });
            }
          }
          
          if (Date.now() - startTime > maxWaitTime) {
            clearInterval(checkToken);
            resolve({ success: false });
          }
        }, 1000);
      });
    } catch (error) {
      console.error("Microsoft login error:", error);
      toast.error("Microsoft innlogging feilet");
      return { success: false };
    }
  };

  const logout = () => {
    localStorage.removeItem("lexy_token");
    setToken(null);
    setUser(null);
    const lang = localStorage.getItem('lexy_plugin_lang') || 'nb';
    toast.info(lang === 'nb' ? "Logget ut" : "Logged out");
  };

  const isAuthenticated = !!token && !!user;

  return (
    <WordAddinAuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      loginWithGoogle, 
      loginWithMicrosoft, 
      logout, 
      loading, 
      isAuthenticated 
    }}>
      {children}
    </WordAddinAuthContext.Provider>
  );
};

// Main App Content - switches between login and main app
const WordAddinContent = () => {
  const { isAuthenticated, loading } = useWordAddinAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Laster...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <MainApp />;
};

function WordAddinApp() {
  return (
    <div className="word-addin-app min-h-screen bg-background text-foreground">
      <WordAddinAuthProvider>
        <WordAddinContent />
      </WordAddinAuthProvider>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default WordAddinApp;
